import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, ModalOverlay } from '@chakra-ui/react';
import { useAppSelector, useAppDispatch } from 'app/store';
import { changeEmailEffect, verifyEmailEffect } from 'app/store/Auth/effects';
import { Link } from 'types/api.types';
import { getChangeEmailLink } from 'app/store/Auth/selectors';
import { useFetchValidationSchema } from 'app/utils/hooks';
import { transformSchemaToYup } from 'app/utils/schemaToYup';
import { AnyObjectSchema } from 'yup';
import {
  defaultValues,
  errorMessages,
} from 'app/components/Settings/EmailChangeDialogForm/utils';
import { EmailChangeDialogForm } from '../EmailChangeDialogForm';
import {
  EmailVerificationDialogForm,
  EmailVerificationForm,
} from '../EmailVerificationDialogForm';
import { useModalProps } from '../useModalProps';

interface EmailChangeForm {
  password: string;
  email: string;
}

export interface EmailChangeFormComponentProps {
  changeEmail: (
    values: EmailChangeForm
  ) => Promise<{ verifyLink: Link | null }>;
  verifyEmail: (values: EmailVerificationForm) => Promise<void>;
  showVerificationForm: boolean;
  onChangeSuccess: (newEmail: string) => void;
  onClose: () => void;
  open: boolean;
  isLoading: boolean;
  validationSchema?: AnyObjectSchema;
}

export const EmailChangeDialogComponent: React.FC<EmailChangeFormComponentProps> = ({
  changeEmail,
  verifyEmail,
  onClose,
  open,
  showVerificationForm,
  onChangeSuccess,
  isLoading,
  validationSchema,
}) => {
  const { modalProps, contentProps } = useModalProps(open);

  return (
    <Modal
      isOpen={open}
      closeOnOverlayClick={!showVerificationForm}
      closeOnEsc={!showVerificationForm}
      onClose={onClose}
      {...modalProps}
    >
      <ModalOverlay />
      {showVerificationForm ? (
        <EmailVerificationDialogForm
          verifyEmail={verifyEmail}
          onClose={onClose}
          modalContentProps={contentProps}
        />
      ) : (
        <EmailChangeDialogForm
          changeEmail={changeEmail}
          onClose={onClose}
          onSuccess={onChangeSuccess}
          isLoading={isLoading}
          validationSchema={validationSchema}
          modalContentProps={contentProps}
        />
      )}
    </Modal>
  );
};

export interface EmailChangeFormProps {
  onClose: () => void;
  open: boolean;
}

export const EmailChangeDialog: React.FC<EmailChangeFormProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const [showVerificationForm, setShowVerificationForm] = useState(false);
  const [newEmail, setNewEmail] = useState('');

  const changeEmailLink = useAppSelector(getChangeEmailLink);

  const {
    validationSchema,
    validationSchemaFetched,
  } = useFetchValidationSchema({ link: changeEmailLink });

  const yupValidationSchema = useMemo(
    () =>
      validationSchema &&
      transformSchemaToYup(validationSchema, defaultValues, errorMessages),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [validationSchema, errorMessages]
  );

  const changeEmail = useCallback(
    (values: EmailChangeForm) => dispatch(changeEmailEffect(values)).unwrap(),
    [dispatch]
  );

  const onChangeSuccess = useCallback((newEmail: string) => {
    setNewEmail(newEmail);
    setShowVerificationForm(true);
  }, []);

  const verifyEmail = useCallback(
    (values: EmailVerificationForm) =>
      dispatch(
        verifyEmailEffect({
          code: values.token,
          newEmail,
        })
      ).unwrap(),
    [dispatch, newEmail]
  );

  useEffect(() => {
    if (open) {
      setShowVerificationForm(false);
    }
  }, [open]);

  return (
    <EmailChangeDialogComponent
      open={open}
      onClose={onClose}
      changeEmail={changeEmail}
      verifyEmail={verifyEmail}
      onChangeSuccess={onChangeSuccess}
      showVerificationForm={showVerificationForm}
      isLoading={!validationSchemaFetched}
      validationSchema={yupValidationSchema}
    />
  );
};
